import React, { useEffect, useState } from 'react';
import { Platform, View } from 'react-native';

import { ActivityIndicator, Button, Modal, Text } from '@/components/ui';
import { documentFileUrl } from '@/hooks/use-document-listing';
import {
	downloadDocument,
	fetchDocumentObjectUrl,
	isImage,
} from '@/utils/document-download';

/**
 * In-app preview of a stored document (issue #373).
 *
 * The bytes are fetched with our auth header and handed to the viewer as a BLOB URL —
 * `<iframe src>` and `<img src>` can't send an Authorization header, so pointing them at
 * the proxy endpoint directly would 401. See `utils/document-download.ts`.
 *
 * The blob is revoked on unmount; without that, every document opened would stay resident
 * for the life of the page.
 *
 * ## Why a file id and nothing else
 *
 * This used to take an `associationId` plus optional `previewUrl`/`downloadUrl` overrides,
 * purely because the old store served an account's owner folder from a different route
 * than an association's. Every file now streams from ONE global `/documents/file/:id`, which proves
 * the file's ancestry server-side, so the id is the whole address and the overrides have
 * nothing left to override.
 */

interface Props {
	visible: boolean;
	fileId: string;
	fileName: string;
	extension: string;
	onDismiss: () => void;
}

export const DocumentPreviewModal: React.FC<Props> = ({
	visible, fileId, fileName, extension, onDismiss,
}) => {
	const url = documentFileUrl(fileId);
	const [objectUrl, setObjectUrl] = useState<string | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [downloading, setDownloading] = useState(false);

	useEffect(() => {
		if (!visible || !fileId) return;
		let cancelled = false;
		let created: string | null = null;

		setObjectUrl(null);
		setError(null);

		(async () => {
			try {
				const blobUrl = await fetchDocumentObjectUrl(url);
				created = blobUrl;
				// A late resolve after close would leak the blob and set state on an
				// unmounted view, so drop it and revoke immediately.
				if (cancelled) { URL.revokeObjectURL(blobUrl); return; }
				setObjectUrl(blobUrl);
			} catch (e) {
				if (!cancelled) setError(e instanceof Error ? e.message : String(e));
			}
		})();

		return () => {
			cancelled = true;
			if (created) URL.revokeObjectURL(created);
		};
	}, [visible, fileId, url]);

	const onDownload = async () => {
		setDownloading(true);
		try {
			await downloadDocument(url, fileName);
		} catch (e) {
			setError(e instanceof Error ? e.message : String(e));
		} finally {
			setDownloading(false);
		}
	};

	return (
		/* dismissOnBackdrop is left at its project default (false) — a misclick shouldn't
		   close a document someone is reading. */
		<Modal visible={visible} onDismiss={onDismiss}>
			<View style={{ gap: 12, minWidth: 320 }}>
				<Text style={{ fontSize: 15, fontWeight: '700' }} numberOfLines={2}>{fileName}</Text>

				{error && (
					<Text style={{ color: '#c62828', fontSize: 13 }}>{error}</Text>
				)}

				{!error && !objectUrl && (
					<View style={{ alignItems: 'center', padding: 32 }}>
						<ActivityIndicator />
						<Text style={{ marginTop: 8, fontSize: 12, color: '#666' }}>Loading document…</Text>
					</View>
				)}

				{/* IMAGES ONLY. PDFs and text open in their own tab via
				    `openDocumentInNewTab` — Chrome refuses to run its PDF viewer inside a
				    sandboxed iframe, and un-sandboxing one to make a document render is the
				    wrong trade. With no iframe here, there's no sandbox to get wrong. */}
				{objectUrl && Platform.OS === 'web' && isImage(extension) && (
					/* eslint-disable-next-line @typescript-eslint/no-explicit-any -- raw <img> is web-only here */
					React.createElement('img' as any, {
						src: objectUrl,
						alt: fileName,
						style: { maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' },
					})
				)}

				{objectUrl && Platform.OS === 'web' && !isImage(extension) && (
					<Text style={{ fontSize: 13, color: '#666' }}>
						This file type opens in a new tab. Use Download if the tab was blocked.
					</Text>
				)}

				{objectUrl && Platform.OS !== 'web' && (
					<Text style={{ fontSize: 13, color: '#666' }}>
						Preview is web-only. Use Download to open this document.
					</Text>
				)}

				<View style={{ flexDirection: 'row', gap: 8, justifyContent: 'flex-end' }}>
					<Button mode="outlined" onPress={onDownload} disabled={downloading}>
						{downloading ? 'Downloading…' : 'Download'}
					</Button>
					<Button mode="contained" onPress={onDismiss}>Close</Button>
				</View>
			</View>
		</Modal>
	);
};
